import secureApi from '../../utils/secureApi';

export interface PlayerStatItem {
  _id: string;
  player: {
    _id: string;
    name: string;
    shortName?: string;
    profile?: string;
  };
  team: {
    _id: string;
    name: string;
    shortName?: string;
    logo?: string;
  };
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
  minutesPlayed: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlayerStat {
  _id: string;
  ageCategory: {
    _id: string;
    ageCategory: string;
  };
  matchNum: number;
  playerStats: PlayerStatItem[];
}

export interface FlattenedPlayerStat extends PlayerStatItem {
  matchNum: number;
  ageCategory: {
    _id: string;
    ageCategory: string;
  };
}

export interface PlayerStatsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: PlayerStat[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}

// Get all player stats for a tournament
export const getAllPlayerStats = async (
  tournamentId: string,
  page: number = 1,
  pageSize: number = 10,
  search: string = ''
): Promise<PlayerStatsResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (search) {
    params.append('search', search);
  }

  const response = await secureApi.get<any>(
    `/api/v2/tournaments/admin/get-all-player-stats/${tournamentId}?${params.toString()}`
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
};

// Get stats for a single player within a tournament
export const getPlayerStatsByPlayer = async (
  tournamentId: string,
  teamId: string,
  playerId: string,
  ageCategory?: string,
  page: number = 1,
  pageSize: number = 10,
  tournamentSeason?: string,
  sport?: string
): Promise<any> => {
  if (!tournamentId || !teamId || !playerId) {
    throw new Error(`Missing required parameters: tournamentId=${tournamentId}, teamId=${teamId}, playerId=${playerId}`);
  }
  
  if (!tournamentSeason) {
    throw new Error(`tournamentSeason is required for getPlayerStatsByPlayer API call. Provided: tournamentId=${tournamentId}, teamId=${teamId}, playerId=${playerId}`);
  }
  
  const params = new URLSearchParams();
  if (ageCategory) {
    params.append('ageCategory', ageCategory);
  }
  params.append('pageNum', page.toString());
  params.append('pageSize', pageSize.toString());
  if (sport) {
    params.append('sport', sport);
  }
  
  const queryString = params.toString();
  let url = `/api/v2/tournaments/admin/get-player-stats/${tournamentId}/${teamId}/${playerId}/${tournamentSeason}`;
  if (queryString) {
    url += `?${queryString}`;
  }
  
  const response = await secureApi.get<any>(url);
  
  if (response && response.data) {
    return response.data;
  }
  return response;
};

// Get a single player stat entry by its ID
export const getPlayerStatById = async (statId: string, sport?: string): Promise<any> => {
  let url = `/api/v2/tournaments/admin/player-stats/get-by-id/${statId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  // secureApi returns response.data from axios, which is the full API response
  if (response && response.data) {
    return response.data;
  }
  return response;
};

// Add a player stat entry
export const addPlayerStat = async (payload: any, sport?: string): Promise<any> => {
  let url = `/api/v2/tournaments/admin/player-stats/add`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.post<any>(url, payload);
  return response;
};

// Edit an existing player stat entry by its id
export const editPlayerStat = async (id: string, payload: any, sport?: string): Promise<any> => {
  let url = `/api/v2/tournaments/admin/player-stats/edit/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.post<any>(url, payload);
  return response;
};

// Delete a player stat entry by match ID
export const deletePlayerStat = async (matchId: string, sport?: string): Promise<any> => {
  let url = `/api/v2/tournaments/admin/player-stats/delete/${matchId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.post<any>(url, {});
  return response;
};

// Bulk upload interfaces (matching fixtures)
export interface BulkUploadPlayerStatsRequest {
  fileKey: string;
}

// Bulk upload player stats
export const bulkUploadPlayerStats = async (
  tournamentId: string, 
  data: BulkUploadPlayerStatsRequest,
  tournamentSeasonId?: string,
  sport?: string
): Promise<any> => {
  // Build URL with tournamentSeasonId as path parameter if provided
  let url = `/api/v2/tournaments/admin/player-stats/bulk-upload/${tournamentId}`;
  if (tournamentSeasonId) {
    url += `/${tournamentSeasonId}`;
  }
  
  // Add sport as query parameter if provided
  // Always use '?' for the first query parameter, regardless of whether tournamentSeasonId is in path
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  
  const response = await secureApi.post<any>(url, data);
  return response;
};
